import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Crosshair, Link2, MapPin, Plus, Trash2, Star, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getOfficeLocations,
  createOfficeLocation,
  updateOfficeLocation,
  deactivateOfficeLocation,
  getAllCompanies,
  getProjectsByCompanyId
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/**
 * Where office staff may mark their own attendance from.
 *
 * Projects already carry their own coordinates and radius for site staff; this
 * is the other half. Nothing existed for it, because a builder's registered
 * address is frequently a registrar's or an accountant's and measuring
 * attendance against it would fail everybody.
 *
 * Two things this screen has to say out loud, because both are surprising:
 *
 *   With NO office configured, nobody is held to anything. The geofence has
 *   nothing to measure against and lets office staff through as unchecked. That
 *   is deliberate — a missing configuration must stay distinguishable from a
 *   violation — but it means adding the first office switches enforcement on for
 *   everyone assigned to it.
 *
 *   The radius decides whether somebody can record their workday at all. This is
 *   an attendance policy, not a preference.
 */

const DEFAULT_CENTRE = [20.5937, 78.9629]; // Geographic centre of India.

const pin = L.divIcon({
  className: '',
  html: `<span style="display:block;width:18px;height:18px;border-radius:50% 50% 50% 0;
    background:#0f766e;transform:rotate(-45deg);border:2px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,.4)"></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 18]
});

const OfficeLocations = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ['officeLocations'],
    queryFn: () => getOfficeLocations()
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['officeLocations'] });

  const save = useMutation({
    mutationFn: (values) =>
      values.id ? updateOfficeLocation(values.id, values) : createOfficeLocation(values),
    onSuccess: (saved) => {
      refresh();
      setEditing(null);
      const applied = saved?.applied_projects ?? [];
      toast.success(
        applied.length
          ? `Saved. Punch pin also set on ${applied.map((project) => project.name).join(', ')}.`
          : 'Office location saved'
      );
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not save the office location')
  });

  const remove = useMutation({
    mutationFn: ({ id, clearProjectPins }) =>
      deactivateOfficeLocation(id, { clear_project_pins: clearProjectPins }),
    onSuccess: (response) => {
      setRemoving(null);
      refresh();
      toast.success(response?.message || 'Office removed');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not remove the office')
  });

  const offices = data?.data ?? [];

  return (
    <Layout>
      <Header data={{ name: 'Office Locations' }} />

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <div className="max-w-2xl">
            <h2 className="text-sm font-medium text-gray-800">
              Where office staff can mark attendance
            </h2>
            <Callout tone="info" collapsible icon={<Info size={14} />} title="How office pins work" className="mt-2">
              Drop a pin from your location, a maps link or a click. Staff punch against their project&apos;s pin; Overall is only for people with no assignment.
            </Callout>
            {offices.length === 0 && (
              <p className="mt-2 rounded border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
                No office is configured, so nobody is being restricted yet. Adding
                the first one turns the check on for every employee assigned to it.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing({})}
            className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white"
          >
            <Plus size={15} /> Add office
          </button>
        </div>

        {isFetching ? (
          <Loader />
        ) : offices.length === 0 ? null : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {offices.map((office) => (
              <div
                key={office.id}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              >
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin size={15} className="text-brand-700" />
                      <span className="font-medium text-gray-900">{office.name}</span>
                      {office.is_primary && (
                        <span className="flex items-center gap-1 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand-700">
                          <Star size={9} /> Primary
                        </span>
                      )}
                    </div>
                    {office.address && (
                      <p className="mt-0.5 text-xs text-gray-500">{office.address}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {office.radius_m} m radius ·{' '}
                      {office.assigned_employees === 1
                        ? '1 employee held to it'
                        : `${office.assigned_employees ?? 0} employees held to it`}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(office)}
                      className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => setRemoving(office)}
                      className="rounded border border-danger-border px-2 py-1 text-xs text-danger-fg"
                      aria-label={`Remove ${office.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <div style={{ height: 200 }}>
                  <MapContainer
                    center={[Number(office.lat), Number(office.lng)]}
                    zoom={15}
                    scrollWheelZoom={false}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Circle
                      center={[Number(office.lat), Number(office.lng)]}
                      radius={Number(office.radius_m) || 200}
                      pathOptions={{ color: '#0f766e', weight: 1, fillOpacity: 0.08 }}
                    />
                    <Marker position={[Number(office.lat), Number(office.lng)]} icon={pin} />
                  </MapContainer>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {removing && (
        <RemoveOfficeModal
          office={removing}
          busy={remove.isPending}
          onClose={() => setRemoving(null)}
          onConfirm={(clearProjectPins) =>
            remove.mutate({ id: removing.id, clearProjectPins })
          }
        />
      )}

      {editing && (
        <OfficeModal
          office={editing}
          busy={save.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(values) => save.mutate({ ...values, id: editing.id })}
        />
      )}
    </Layout>
  );
};

/**
 * Removing an office. Its pin may also sit on projects (copied there by
 * "apply to company / project"), and staff on those projects punch against the
 * project pin — so deleting only the office left Tezpur accepting punches
 * through Casa Felicia (UAT, 30 Sep). Those projects are named here, with
 * clearing them ticked by default.
 */
const RemoveOfficeModal = ({ office, busy, onClose, onConfirm }) => {
  const pinned = office.pinned_projects ?? [];
  const [clearPins, setClearPins] = useState(true);
  const count = office.assigned_employees ?? 0;
  return (
    <Modal name={`Remove ${office.name}`} onClose={onClose} width="480px">
      <div className="space-y-3 text-sm text-gray-700">
        <p>
          {count
            ? `${count} employee(s) are held to this office. Removing it stops that check for them.`
            : 'Staff will no longer be able to punch in from this office.'}
        </p>
        {pinned.length > 0 && (
          <label className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2">
            <input
              type="checkbox"
              checked={clearPins}
              onChange={(e) => setClearPins(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-700"
            />
            <span className="text-xs text-warning-fg">
              <strong>Also remove this pin from {pinned.map((p) => p.name).join(', ')}.</strong>{' '}
              Staff on {pinned.length === 1 ? 'that project' : 'those projects'} punch against the
              project&apos;s pin, so leaving it keeps this place working for them. Set the
              project&apos;s real site pin afterwards.
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(pinned.length > 0 && clearPins)}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {busy ? 'Removing…' : 'Remove office'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const OfficeModal = ({ office, busy, onClose, onSubmit }) => {
  const [name, setName] = useState(office.name ?? '');
  const [address, setAddress] = useState(office.address ?? '');
  const [radius, setRadius] = useState(String(office.radius_m ?? 200));
  const [isPrimary, setIsPrimary] = useState(Boolean(office.is_primary));
  const [point, setPoint] = useState(
    office.lat != null ? { lat: Number(office.lat), lng: Number(office.lng) } : null
  );
  const [mapsLink, setMapsLink] = useState('');
  const [locating, setLocating] = useState(false);
  const [scope, setScope] = useState('overall');
  const [companyId, setCompanyId] = useState('');
  const [projectId, setProjectId] = useState('');

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });
  const companies = Array.isArray(companiesData)
    ? companiesData
    : companiesData?.data ?? companiesData?.companies ?? [];

  const { data: projectsData } = useQuery({
    queryKey: ['project_byId', companyId],
    queryFn: () => getProjectsByCompanyId(companyId),
    enabled: Boolean(companyId)
  });
  const projects = Array.isArray(projectsData)
    ? projectsData
    : projectsData?.data ?? [];

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('This browser cannot read your current location');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPoint({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        toast.error(err?.message || 'Could not read your current location');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const applyMapsLink = () => {
    const parsed = parseMapsOrCoords(mapsLink);
    if (!parsed) {
      toast.error('Paste a Google Maps link or lat, lng');
      return;
    }
    setPoint(parsed);
  };

  const submit = () => {
    if (!name.trim()) {
      toast.error('Give the office a name');
      return;
    }
    if (!point) {
      toast.error('Use current location, paste a maps link, or click the map');
      return;
    }
    const radiusValue = Number(radius);
    if (!Number.isFinite(radiusValue) || radiusValue < 25 || radiusValue > 5000) {
      toast.error('Radius must be between 25 and 5000 metres');
      return;
    }
    if (scope === 'company' && !companyId) {
      toast.error('Pick the company this pin should apply to');
      return;
    }
    if (scope === 'project' && !projectId) {
      toast.error('Pick the project this pin should apply to');
      return;
    }
    onSubmit({
      name: name.trim(),
      address: address.trim() || null,
      lat: point.lat,
      lng: point.lng,
      radius_m: Math.round(radiusValue),
      is_primary: isPrimary,
      ...(scope === 'company' ? { apply_to_company_id: Number(companyId) } : {}),
      ...(scope === 'project' ? { apply_to_project_ids: [Number(projectId)] } : {})
    });
  };

  return (
    <Modal
      name={office.id ? `Edit ${office.name}` : 'Add office location'}
      onClose={onClose}
      width="720px"
    >
      <div className="space-y-3">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Head Office"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Address (optional)">
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Place the pin">
          <div className="mb-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={locating}
              className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-3 py-1.5 text-xs font-medium text-brand-800 disabled:opacity-60"
            >
              <Crosshair size={13} />
              {locating ? 'Reading location…' : 'Use my current location'}
            </button>
          </div>
          <div className="mb-2 flex gap-2">
            <input
              value={mapsLink}
              onChange={(e) => setMapsLink(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyMapsLink();
                }
              }}
              placeholder="Paste a Google Maps link or 26.62, 92.79"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={applyMapsLink}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-700"
            >
              <Link2 size={13} /> Drop pin
            </button>
          </div>
          <div
            className="overflow-hidden rounded-lg border border-gray-300"
            style={{ height: 260 }}
          >
            <MapContainer
              center={point ? [point.lat, point.lng] : DEFAULT_CENTRE}
              zoom={point ? 16 : 5}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickToPlace onPick={setPoint} />
              <RecenterOnPoint point={point} />
              {point && (
                <>
                  <Circle
                    center={[point.lat, point.lng]}
                    radius={Number(radius) || 200}
                    pathOptions={{ color: '#0f766e', weight: 1, fillOpacity: 0.08 }}
                  />
                  <Marker position={[point.lat, point.lng]} icon={pin} />
                </>
              )}
            </MapContainer>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {point
              ? `Placed at ${point.lat.toFixed(5)}, ${point.lng.toFixed(5)} — click the map to nudge it.`
              : 'Use current location, paste a link, or click the map.'}
          </p>
        </Field>

        <Field label="Radius (metres)">
          <input
            type="number"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Wide enough to cover the building and its car park.
          </p>
        </Field>

        <Field label="Apply this pin to">
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="office-scope"
                checked={scope === 'overall'}
                onChange={() => setScope('overall')}
                className="mt-0.5 accent-brand-700"
              />
              <span>
                <span className="font-medium">Overall</span>
                <span className="block text-xs text-gray-500">
                  Builder-wide office only. Assigned staff still punch at their
                  project pin.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="office-scope"
                checked={scope === 'company'}
                onChange={() => setScope('company')}
                className="mt-0.5 accent-brand-700"
              />
              <span>
                <span className="font-medium">This company</span>
                <span className="block text-xs text-gray-500">
                  Copy the pin onto every project under the company.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="office-scope"
                checked={scope === 'project'}
                onChange={() => setScope('project')}
                className="mt-0.5 accent-brand-700"
              />
              <span>
                <span className="font-medium">This project</span>
                <span className="block text-xs text-gray-500">
                  Copy the pin onto one project. Assigned staff then punch here.
                </span>
              </span>
            </label>

            {scope !== 'overall' && (
              <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                <select
                  value={companyId}
                  onChange={(e) => {
                    setCompanyId(e.target.value);
                    setProjectId('');
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Select company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name || company.name}
                    </option>
                  ))}
                </select>
                {scope === 'project' && (
                  <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    disabled={!companyId}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Select project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </Field>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="h-4 w-4 accent-brand-700"
          />
          Make this the primary office
          <span className="text-xs text-gray-500">
            (used for employees with no office named)
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Save office'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/** Click anywhere on the map to place the pin. */
const ClickToPlace = ({ onPick }) => {
  useMapEvents({
    click: (event) => onPick({ lat: event.latlng.lat, lng: event.latlng.lng })
  });
  return null;
};

const RecenterOnPoint = ({ point }) => {
  const map = useMap();
  useEffect(() => {
    if (point) map.setView([point.lat, point.lng], 16);
  }, [map, point]);
  return null;
};

function inRange(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

/** Google Maps share links, @lat,lng, or a raw coordinate pair. */
function parseMapsOrCoords(input) {
  const text = String(input || '').trim();
  if (!text) return null;

  const at = text.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (at && inRange(Number(at[1]), Number(at[2]))) {
    return { lat: Number(at[1]), lng: Number(at[2]) };
  }

  const bang = text.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (bang && inRange(Number(bang[1]), Number(bang[2]))) {
    return { lat: Number(bang[1]), lng: Number(bang[2]) };
  }

  const query = text.match(/[?&](?:q|query|ll|center)=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/i);
  if (query && inRange(Number(query[1]), Number(query[2]))) {
    return { lat: Number(query[1]), lng: Number(query[2]) };
  }

  const pair = text.match(/(-?\d+\.?\d*)\s*[, ]\s*(-?\d+\.?\d*)/);
  if (pair && inRange(Number(pair[1]), Number(pair[2]))) {
    return { lat: Number(pair[1]), lng: Number(pair[2]) };
  }

  return null;
}

const Field = ({ label, children }) => (
  <div>
    <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
    {children}
  </div>
);

export default OfficeLocations;
